// =====================================================
// COMPOSANT: FilterBar — barre de filtres du dashboard stats (RETOUCHE 1)
// =====================================================
// Édition · Plage de dates (date début / date fin) · toggle Comparaison N-1.
// RETOUCHE 1 : suppression des filtres Segment, Période/presets et du toggle
// « Inscrits définitifs ». Le pilotage se fait par la plage [début, fin] ; N-1 =
// la même plage décalée d'un an. Design system crm-4l (zinc). Le state remonte au
// parent (DashboardStatsPage) → pilote toutes les sections.

import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Toggle } from '@/components/ui/toggle';
import type { EditionRef } from '@/lib/dashboard-stats';

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10.5px] font-semibold uppercase tracking-wider text-zinc-400">
      {children}
    </span>
  );
}

interface FilterBarProps {
  editions: EditionRef[];
  editionId: string | null;
  onEditionChange: (id: string) => void;
  /** Date de début 'yyyy-mm-dd'. */
  startDate: string;
  onStartDateChange: (v: string) => void;
  /** Date de fin 'yyyy-mm-dd'. */
  endDate: string;
  onEndDateChange: (v: string) => void;
  showN1: boolean;
  onShowN1Change: (b: boolean) => void;
  /** Libellé de la plage N-1 (ex. « 10/04/2025 → 09/07/2025 »), pour le toggle. */
  n1RangeLabel: string;
}

export function FilterBar({
  editions,
  editionId,
  onEditionChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  showN1,
  onShowN1Change,
  n1RangeLabel,
}: FilterBarProps) {
  // La fin ne peut pas précéder le début, ni le début suivre la fin (bornes du
  // date-picker natif — feedback immédiat sans invalider silencieusement).
  return (
    <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
      {/* Édition */}
      <div className="flex flex-col gap-2">
        <FieldLabel>Édition</FieldLabel>
        <Select
          className="h-9 w-auto min-w-[11rem]"
          value={editionId ?? ''}
          onChange={(e) => onEditionChange(e.target.value)}
          options={editions.map((ed) => ({ value: ed.id, label: ed.name }))}
        />
      </div>

      {/* Plage de dates */}
      <div className="flex flex-col gap-2">
        <FieldLabel>Du</FieldLabel>
        <Input
          type="date"
          className="h-9 w-auto"
          value={startDate}
          max={endDate}
          onChange={(e) => onStartDateChange(e.target.value)}
          aria-label="Date de début"
        />
      </div>
      <div className="flex flex-col gap-2">
        <FieldLabel>Au</FieldLabel>
        <Input
          type="date"
          className="h-9 w-auto"
          value={endDate}
          min={startDate}
          onChange={(e) => onEndDateChange(e.target.value)}
          aria-label="Date de fin"
        />
      </div>

      {/* Comparaison N-1 (valeur = plage année-1) */}
      <div className="flex flex-col gap-2">
        <FieldLabel>Comparaison</FieldLabel>
        {/* Pas de <button> englobant : Toggle rend déjà son propre <button>
            (imbrication interdite en HTML). Le libellé est un <span> cliquable. */}
        <div className="flex items-center gap-2 text-sm font-medium text-zinc-700">
          <Toggle checked={showN1} onChange={onShowN1Change} />
          <span
            className="cursor-pointer select-none"
            onClick={() => onShowN1Change(!showN1)}
            title={`N-1 = ${n1RangeLabel}`}
          >
            Afficher N-1
            <span className="ml-1 text-xs font-normal text-zinc-400">({n1RangeLabel})</span>
          </span>
        </div>
      </div>
    </div>
  );
}
